import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PencilSimple, Printer, X, Receipt as ReceiptIcon } from '@phosphor-icons/react';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

const statusColors = {
  paid: 'bg-[#D1FAE5] text-[#065F46]',
  partial: 'bg-[#FEF3C7] text-[#92400E]',
  unpaid: 'bg-[#FEE2E2] text-[#991B1B]',
  pending: 'bg-[#FEE2E2] text-[#991B1B]',
  overpaid: 'bg-[#EDE9FE] text-[#5B21B6]',
};

/**
 * InvoiceViewDialog
 *
 * Fullscreen read-only popup that shows a complete invoice — header, items,
 * totals and payment history. Surfaces an "Edit Invoice" button that
 * navigates straight to `/invoices/edit/{id}` so the user can amend the
 * bill if needed without losing context.
 *
 * Props:
 *   open         – controlled visibility
 *   onOpenChange – setter
 *   invoiceNumber – the bill number to fetch (e.g. "INV-00037"). The
 *                  component will look it up via GET /api/invoices and
 *                  derive the id internally.
 */
export default function InvoiceViewDialog({ open, onOpenChange, invoiceNumber }) {
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !invoiceNumber) return;
    setInvoice(null);
    setError(null);
    setLoading(true);
    // The invoices list endpoint already returns the derived payment state
    // (balance_due, payment_history) for every row. We just locate the one
    // matching our invoice number — avoids needing a dedicated /by-number
    // endpoint and keeps the source of truth single.
    api.get('/invoices')
      .then(r => {
        const inv = (r.data || []).find(i => i.invoice_number === invoiceNumber);
        if (!inv) {
          setError(`Bill ${invoiceNumber} not found.`);
        } else {
          setInvoice(inv);
        }
      })
      .catch(() => setError('Failed to load bill'))
      .finally(() => setLoading(false));
  }, [open, invoiceNumber]);

  const handleEdit = () => {
    if (!invoice) return;
    onOpenChange(false);
    navigate(`/invoices/edit/${invoice.id}`);
  };

  const handlePrint = () => {
    // Defer to the route's own print — simpler than re-implementing the
    // styled receipt template here, and stays consistent with the rest of
    // the app's print output.
    if (!invoice) return;
    onOpenChange(false);
    navigate(`/invoices/edit/${invoice.id}?print=1`);
  };

  const svc = invoice?.items?.reduce((s, i) => s + (i.service_charge || 0) * (i.quantity || 0), 0) || 0;
  const govt = invoice?.items?.reduce((s, i) => s + (i.govt_fee || 0) * (i.quantity || 0), 0) || 0;
  const discount = invoice?.discount || 0;
  const due = invoice?.balance_due || 0;
  const paid = invoice?.amount_paid || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!inset-0 !left-0 !top-0 !translate-x-0 !translate-y-0 w-screen h-screen max-w-none max-h-none p-0 rounded-none border-0 flex flex-col overflow-hidden"
        data-testid="invoice-view-dialog"
      >
        <DialogHeader className="px-8 pt-5 pb-3 border-b border-[#E4E4E7] bg-[#FAFAFA] shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="flex items-center gap-2 text-base">
                <ReceiptIcon size={20} weight="duotone" className="text-[#3B82F6]" />
                {invoiceNumber || 'Bill'}
                {invoice?.status && (
                  <Badge className={`rounded-md text-[10px] capitalize ${statusColors[invoice.status] || ''}`} variant="outline">
                    {invoice.status}
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription className="text-xs text-[#52525B]">
                {invoice ? `${invoice.customer_name} · ${new Date(invoice.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}` : 'Loading bill...'}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrint} disabled={!invoice} className="rounded-lg" data-testid="bill-view-print-btn">
                <Printer size={14} className="mr-1.5" /> Print
              </Button>
              <Button size="sm" onClick={handleEdit} disabled={!invoice} className="rounded-lg bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white" data-testid="bill-view-edit-btn">
                <PencilSimple size={14} className="mr-1.5" /> Edit Invoice
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {loading && <p className="text-center text-sm text-[#A1A1AA] py-12" data-testid="bill-view-loading">Loading bill…</p>}
          {error && <p className="text-center text-sm text-[#EF4444] py-12" data-testid="bill-view-error">{error}</p>}
          {invoice && (
            <div className="max-w-5xl mx-auto space-y-5">
              {/* Summary strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="bill-view-summary">
                <div className="rounded-lg bg-[#F4F4F5] p-3">
                  <p className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Customer</p>
                  <p className="text-sm font-semibold text-[#0A0A0A] truncate">{invoice.customer_name}</p>
                </div>
                <div className="rounded-lg bg-[#F4F4F5] p-3">
                  <p className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Invoice Date</p>
                  <p className="text-sm font-semibold text-[#0A0A0A]">{new Date(invoice.invoice_date || invoice.created_at).toLocaleDateString('en-IN')}</p>
                </div>
                <div className="rounded-lg bg-[#F4F4F5] p-3">
                  <p className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Bill Total</p>
                  <p className="text-sm font-bold font-mono text-[#0A0A0A]">{fmt(invoice.total)}</p>
                </div>
                <div className={`rounded-lg p-3 ${due > 0 ? 'bg-[#FEF2F2]' : 'bg-[#ECFDF5]'}`}>
                  <p className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Due</p>
                  <p className={`text-sm font-bold font-mono ${due > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{fmt(due)}</p>
                </div>
              </div>

              {/* Items */}
              <div className="rounded-xl border border-[#E4E4E7] overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#FAFAFA] border-[#E4E4E7]">
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Service</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-center w-16">Qty</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Svc Charge</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Govt Fee</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Remarks</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Work Status</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(invoice.items || []).map((it, i) => (
                      <TableRow key={i} className="border-[#E4E4E7]" data-testid={`bill-view-item-${i}`}>
                        <TableCell className="text-xs font-medium text-[#0A0A0A]">{it.service_name}</TableCell>
                        <TableCell className="text-xs text-center font-mono">{it.quantity}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{fmt(it.service_charge)}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{fmt(it.govt_fee)}</TableCell>
                        <TableCell className="text-xs text-[#52525B] max-w-[180px] truncate" title={it.remark || ''}>{it.remark || '—'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="rounded-md text-[10px] capitalize">{it.work_status || 'pending'}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono font-semibold">{fmt(it.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totals + Payment History side-by-side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="rounded-xl border border-[#E4E4E7] p-4 space-y-1.5 text-sm" data-testid="bill-view-totals">
                  <div className="flex justify-between"><span className="text-[#52525B]">Service Charge</span><span className="font-mono">{fmt(svc)}</span></div>
                  <div className="flex justify-between"><span className="text-[#52525B]">Govt Fee</span><span className="font-mono">{fmt(govt)}</span></div>
                  {discount > 0 && (
                    <div className="flex justify-between text-[#10B981]"><span>Discount</span><span className="font-mono">− {fmt(discount)}</span></div>
                  )}
                  <div className="border-t border-[#E4E4E7] my-1" />
                  <div className="flex justify-between font-semibold"><span>Bill Total</span><span className="font-mono">{fmt(invoice.total)}</span></div>
                  <div className="flex justify-between text-[#10B981] font-semibold"><span>Paid</span><span className="font-mono">{fmt(paid)}</span></div>
                  <div className={`flex justify-between font-bold ${due > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>
                    <span>Due</span><span className="font-mono">{fmt(due)}</span>
                  </div>
                  {invoice.notes && (
                    <div className="mt-3 pt-3 border-t border-[#E4E4E7]">
                      <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B] mb-1">Notes</p>
                      <p className="text-xs text-[#52525B] whitespace-pre-wrap">{invoice.notes}</p>
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-[#E4E4E7] p-4" data-testid="bill-view-payments">
                  <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B] mb-2">Payment History</p>
                  {(invoice.payment_history || []).length === 0 ? (
                    <p className="text-xs text-[#A1A1AA] italic py-4 text-center">No payments recorded yet.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {(invoice.payment_history || []).map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-xs bg-[#FAFAFA] rounded-md px-3 py-2" data-testid={`bill-view-payment-${i}`}>
                          <div>
                            <p className="font-mono font-semibold text-[#3B82F6]">{p.receipt_number || `Receipt ${i + 1}`}</p>
                            <p className="text-[10px] text-[#A1A1AA]">{p.payment_date ? new Date(p.payment_date).toLocaleDateString('en-IN') : '—'}</p>
                          </div>
                          <span className="font-mono font-semibold text-[#10B981]">{fmt(p.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="px-8 py-4 border-t border-[#E4E4E7] bg-[#FAFAFA] shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg" data-testid="bill-view-close-btn">
            <X size={14} className="mr-1.5" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
